import React, { FC, memo, useCallback, useState } from 'react'
import cn from 'classnames'
import { useStore } from 'store'
import { observer } from 'mobx-react'
import { SectionContainer } from 'containers/SectionContainer'
import { InnerContainer } from 'containers/InnerContainer'
import { HeadsetInfoItem } from 'components/HeadsetInfoItem'
import { TitleSection } from 'components/TitleSection'

import { useMediaQuery } from 'utils/hooks'
import { staticMultimedia } from 'utils'

import { ReactComponent as HeadsetPointer } from 'assets/images/ellipsePointer.svg'

import styles from './styles.module.scss'

interface IProps {
}

enum HeadsetInfo {
  QLED_Display,
  Eye_Tracking,
  REAL_FOV,
  Hand_Tracking
}

const getHeadsetInfo = (type: HeadsetInfo) => {
  switch (type) {
    case HeadsetInfo.QLED_Display:
      return {
        key: HeadsetInfo.QLED_Display,
        title: 'QLED Display:',
        description: 'Featuring a QLED Mini-Led Fast LCD with 2880 x 2880 per-eye resolution, our displays ensure vivid clarity. Boasting 210nit brightness and 20,000:1 (HDR) local dimming, they offer stunning contrast. Experience fluid visuals with a base 90Hz refresh rate, upgradeable to 120Hz and 144Hz. Our quality control process guarantees that every display is precisely calibrated and manually inspected for dead pixels or inconsistencies.'
      }
    case HeadsetInfo.Eye_Tracking:
      return {
        key: HeadsetInfo.Eye_Tracking,
        title: 'Eye Tracking:',
        description: 'Experience advanced, open-source eye tracking technology at 120Hz, allowing for single-point calibration. Benefit from VR & XR foveated rendering, optimizing performance for where you\'re looking. Dive into gaze analysis and explore heat-map visualizations, enhancing your understanding of how you engage with the virtual world.'
      }
    case HeadsetInfo.REAL_FOV:
      return {
        key: HeadsetInfo.REAL_FOV,
        title: 'REAL FOV:',
        description: 'Unleash your VR field of view with a stunning 125° horizontal and 100° vertical immersion, allowing you to explore the virtual universe with unparalleled freedom. Experience a real and undistorted perspective, complemented by an amazing sweet spot and crystal-clear clarity. Whether you are using virtual monitors or simply immersing yourself in VR simulator experiences, the Somnium VR1 will be the perfect companion.'
      }
    case HeadsetInfo.Hand_Tracking:
      return {
        key: HeadsetInfo.Hand_Tracking,
        title: 'Hand Tracking:',
        description: 'Experience seamless VR & MR interactions with Ultraleap® top-tier hand tracking technology. Boasting a broad 170×170° field of view and a swift refresh rate of 120Hz, it ensures that your hand gestures and movements translate into intuitive controls within the virtual domain. Harness this technology to craft next-generation interactions for your applications.'
      }
  }
}

const UnveilSection: FC<IProps> = props => {
  const width_1024 = useMediaQuery('(max-width: 1024px)')
  const [activeItem, setActiveItem] = useState<number | null>(null)

  const onChangeActiveItem = useCallback((key: number | null) => {
    setActiveItem(key)
  }, [])

  return (
    <SectionContainer withBackgroundImage id={'unveilTheSpectacle'}>
      <InnerContainer>
        <div className={styles.unveilContainer}>

          <div className={styles.headsetImagesBlock}>
            <div className={styles.leftBlock}>

              <TitleSection title={'Built to Amaze & Built to Last'}
                            subTitle={'Enjoy the Visual Brilliance & Modularity of the VR1 Headset'}/>

              <div className={styles.headsetFirstBox}>
                <img
                  src={staticMultimedia.unveilTheSpectacle1}
                  alt="headset"/>
                <HeadsetPointer className={cn({
                  [styles.eyeTrackingPointer]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Eye_Tracking
                })}/>
                <HeadsetPointer className={cn({
                  [styles.QLED_DisplayPointerOne]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.QLED_Display
                })}/>
                <HeadsetPointer className={cn({
                  [styles.QLED_DisplayPointerTwo]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.QLED_Display
                })}/>
                <HeadsetPointer className={cn({
                  [styles.REAL_FOVPointer]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.REAL_FOV
                })}/>
              </div>
              {width_1024
                ? <div className={styles.headsetInfoBlock}>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.QLED_Display)}
                                   changeActiveItem={onChangeActiveItem}/>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Eye_Tracking)}
                                   changeActiveItem={onChangeActiveItem}/>
                </div>
                : null}

            </div>
            <div className={styles.rightBlock}>
              <div className={styles.headsetSecondBox}>
                <img
                  src={staticMultimedia.unveilTheSpectacle2}
                  alt="headset"/>
                <HeadsetPointer className={cn({
                  [styles.handTrackingPointerOne]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Hand_Tracking
                })}/>
                <HeadsetPointer className={cn({
                  [styles.handTrackingPointerTwo]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Hand_Tracking
                })}/>
              </div>
              {width_1024
                ? <div className={styles.headsetInfoBlock}>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.REAL_FOV)} changeActiveItem={onChangeActiveItem}/>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Hand_Tracking)}
                                   changeActiveItem={onChangeActiveItem}/>
                </div>
                : null}
            </div>
          </div>

          {!width_1024
            ? <div className={styles.headsetInfoBlock}>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.QLED_Display)} changeActiveItem={onChangeActiveItem}/>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Eye_Tracking)} changeActiveItem={onChangeActiveItem}/>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.REAL_FOV)} changeActiveItem={onChangeActiveItem}/>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Hand_Tracking)} changeActiveItem={onChangeActiveItem}/>
            </div>
            : null}
        </div>
      </InnerContainer>
    </SectionContainer>
  )
}

export default memo(UnveilSection)
